import { api, ep, setActiveOrg } from "aldoo-ra/API"
import axios from "axios"

const orgApi = api("organizations")

export async function createOrg(name) {
    return orgApi({ create: { name } })
}

export async function updateOrg(orgId, patch) {
    // Back-compat: older callers pass just a string name.
    if (typeof patch === "string") return orgApi({ update: { orgId, name: patch } })
    return orgApi({ update: { orgId, ...(patch || {}) } })
}

export async function getOrg(orgId) {
    return orgApi({ get: { orgId } })
}

export async function listOrgs() {
    return orgApi({ list: {} })
}

export async function sendInvites(orgId, emails, teamIds = []) {
    return orgApi({ invite: { orgId, emails, teamIds } })
}

export async function orgInviteLink(orgId, { teamIds = [], regenerate = false } = {}) {
    return orgApi({ invite_link: { orgId, teamIds, regenerate } })
}

export async function removeMember(orgId, userId) {
    return orgApi({ remove: { orgId, userId } })
}

export async function promoteMember(orgId, userId) {
    return orgApi({ promote: { orgId, userId } })
}

export async function demoteMember(orgId, userId) {
    return orgApi({ demote: { orgId, userId } })
}

export async function listMembers(orgId) {
    return orgApi({ members: { orgId } })
}

export async function deleteOrg(orgId) {
    return orgApi({ delete: { orgId } })
}

export async function removeInvite(orgId, inviteId) {
    return orgApi({ invite_remove: { orgId, inviteId } })
}

export async function resendInvite(orgId, inviteId) {
    return orgApi({ invite_resend: { orgId, inviteId } })
}

export async function myOrgInvites() {
    return orgApi({ invites_mine: {} })
}

export async function listTeams(orgId) {
    return orgApi({ team_list: { orgId } })
}

export async function joinTeam(orgId, teamId) {
    return orgApi({ team_join: { orgId, teamId } })
}

export async function leaveTeam(orgId, teamId) {
    return orgApi({ team_leave: { orgId, teamId } })
}

export async function createTeam(orgId, name, opts = {}) {
    return orgApi({ team_create: { orgId, name, visibility: opts.visibility } })
}

export async function deleteTeam(orgId, teamId) {
    return orgApi({ team_delete: { orgId, teamId } })
}

export async function renameTeam(orgId, teamId, name) {
    return orgApi({ team_rename: { orgId, teamId, name } })
}

export async function setTeamMembers(orgId, teamId, members) {
    return orgApi({ team_set_members: { orgId, teamId, members } })
}

export async function getTeam(orgId, teamId) {
    return orgApi({ team_get: { orgId, teamId } })
}

export async function updateTeam(orgId, teamId, patch) {
    return orgApi({ team_update: { orgId, teamId, ...patch } })
}

export async function listTeamMembers(orgId, teamId) {
    return orgApi({ team_members: { orgId, teamId } })
}

export async function addTeamMembers(orgId, teamId, userIds) {
    return orgApi({ team_member_add: { orgId, teamId, userIds } })
}

export async function removeTeamMember(orgId, teamId, userId) {
    return orgApi({ team_member_remove: { orgId, teamId, userId } })
}

export async function setTeamMemberRole(orgId, teamId, userId, role) {
    return orgApi({ team_member_role: { orgId, teamId, userId, role } })
}

export async function setActiveOrgRemote(orgId) {
    return orgApi({ active_set: { orgId } })
}

/**
 * Make an org the active one everywhere at once: request header
 * (setActiveOrg), server-side default (active_set) and every mounted
 * switcher/workspace via the "workspace:org-changed" event. Use this from
 * any place that creates or picks an org — creating from the Organizations
 * page or onboarding used to leave the sidebar switcher on the old org.
 */
export function activateOrg(orgId) {
    const id = orgId ? String(orgId) : null
    setActiveOrg(id)
    if (id) setActiveOrgRemote(id).catch(() => {})
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("workspace:org-changed", { detail: { orgId: id } }))
    }
}

// `deviceID`: the browser's device, for the session an email invite signs in
// with (optional — without it the API makes a device of its own)
export async function acceptInvite(token, deviceID) {
    return api("organizations/invite/accept")({ accept: { token, ...(deviceID ? { deviceID } : {}) } })
}

export async function uploadOrgLogo(orgId, blob, mimeType) {
    const { getValue } = await import("aldoo-ra/Value")
    const credentials = getValue("user-credentials", "user-credentials")
    const ext = mimeType?.split("/")?.[1] || "jpg"
    const formData = new FormData()
    formData.append("logo_upload", blob, `logo.${ext}`)
    formData.append("orgId", orgId)
    const headers = {}
    if (credentials?.auth_token) headers["Authorization"] = credentials.auth_token
    const response = await axios.post(ep("organizations"), formData, { headers })
    return response.data
}

export function orgLogoUrl(orgId, version) {
    const base = `${ep("organizations")}/logo/${orgId}`
    // The logo path is stable across replaces — the version stamp
    // (logoUpdatedAt) defeats the browser/CDN cache after an upload.
    const t = version ? new Date(version).getTime() : 0
    return t ? `${base}?t=${t}` : base
}

export async function uploadTeamIcon(orgId, teamId, blob, mimeType) {
    const { getValue } = await import("aldoo-ra/Value")
    const credentials = getValue("user-credentials", "user-credentials")
    const ext = mimeType?.split("/")?.[1] || "jpg"
    const formData = new FormData()
    formData.append("team_icon_upload", blob, `icon.${ext}`)
    formData.append("orgId", orgId)
    formData.append("teamId", teamId)
    const headers = {}
    if (credentials?.auth_token) headers["Authorization"] = credentials.auth_token
    const response = await axios.post(ep("organizations"), formData, { headers })
    return response.data
}

export function teamIconUrl(orgId, teamId) {
    return `${ep("organizations")}/team-icon/${orgId}/${teamId}`
}
